import React, { useState, useRef, useEffect } from "react";
import { Eye, ChevronDown } from "lucide-react";
import type { WidgetAction } from "@/lib/mechanics/types";

interface POVCharacter {
  name: string;
  id?: string;
  location?: string;
}

interface POVStatusWidgetProps {
  activePov?: string | null;
  povCharacters?: POVCharacter[];
  widgetActions?: WidgetAction[];
  onWidgetAction?: (action: WidgetAction, params: Record<string, string>) => void;
}

export function POVStatusWidget({ activePov, povCharacters, widgetActions, onWidgetAction }: POVStatusWidgetProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  if (!activePov) return null;

  const chars = povCharacters || [];
  const switchAction = widgetActions?.[0];
  const others = chars.filter((c) => c.name !== activePov && c.id !== activePov);

  return (
    <div className="relative" ref={ref}>
      <button
        className="flex items-center gap-1.5 text-xs hover:text-foreground text-muted-foreground transition-colors"
        onClick={() => others.length > 0 && setOpen((v) => !v)}
        title="Current POV character"
      >
        <Eye className="w-3.5 h-3.5" />
        <span className="font-medium">{activePov}</span>
        {others.length > 0 && <ChevronDown className="w-3 h-3" />}
      </button>

      {open && others.length > 0 && switchAction && (
        <div className="absolute top-full left-0 mt-1 z-50 bg-popover border border-border rounded-md shadow-md min-w-[140px] py-1">
          {others.map((c) => (
            <button
              key={c.name || c.id}
              className="w-full text-left px-3 py-1.5 text-xs hover:bg-accent transition-colors"
              onClick={() => {
                onWidgetAction?.(switchAction, { target_character: c.name || c.id || "" });
                setOpen(false);
              }}
            >
              {c.name}
              {c.location && <span className="text-muted-foreground ml-1">· {c.location}</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
